import React, { useState, useRef, useCallback } from "react"
import PropTypes from "prop-types"
import { useInView } from "react-intersection-observer"

import { imagePreloader, getOptimizedImageUrl } from "@commonUtils"
import { useSelector } from "react-redux"

import css from "./ImageLoader.scss"

const _loaded = {}

// This transparent svg will be displayed when height and width props of the image have been passed. This has been done so that Imageloader does not show a broken image  when inview is false
const placeholderSrc = (width, height, viewBox = "") => {
    const _viewbox = viewBox || `0 0 ${width} ${height}`
    return `data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="${_viewbox}"%3E%3C/svg%3E`
}

const ImageLoader = (props) => {
    const imageRef = useRef()
    const [loaded, setLoaded] = useState(_loaded[props.src])
    const [inViewRef, inView] = useInView({
        triggerOnce: true,
        rootMargin: "0px 0px 100% 0px",
    })
    const isBot = useSelector((state) => state.shellReducer?.isBot)
    if (isBot) {
        return (
            <>
                {props.preload && <link rel="preload" as="image" href={props.src} />}
                <img
                    src={props.src}
                    onClick={props.onClick ? props.onClick : null}
                    className={props.className}
                    alt={props.alt}
                    title={props.title}
                    width={props.width}
                    height={props.height}
                />
            </>
        )
    }
    const setRefs = useCallback(
        (node) => {
            imageRef.current = node
            inViewRef(node)
        },
        [inViewRef]
    )

    //image onLoad handler to update state to loaded
    const onLoad = () => {
        if (inView) {
            _loaded[props.src] = true
            setLoaded(true)
        }
    }

    const getImageUrl = () => {
        if (props.optimize) {
            return getOptimizedImageUrl(
                props.src,
                {
                    width: imageRef?.current?.width,
                },
                true
            )
        }
        return props.src
    }

    let { className, loadedClassName, loadingClassName, transparentImage } = props

    className = `${className} ${
        loaded ? loadedClassName : props.width && props.height ? transparentImage : loadingClassName
    }`

    return (
        <>
            {!props.preload && (
                <img
                    ref={setRefs}
                    src={
                        inView
                            ? getImageUrl()
                            : props.width && props.height
                            ? placeholderSrc(props.width, props.height, props.viewBox)
                            : ""
                    }
                    onClick={props.onClick ? props.onClick : null}
                    className={className}
                    onLoad={onLoad}
                    alt={inView ? props.alt : ""}
                    title={props.title}
                    width={props.width}
                    height={props.height}
                />
            )}
            {props.preload && (
                <>
                    {imagePreloader(props.src)}
                    <img
                        src={props.src}
                        onClick={props.onClick ? props.onClick : null}
                        className={props.className}
                        alt={props.alt}
                        title={props.title}
                        width={props.width}
                        height={props.height}
                    />
                </>
            )}
        </>
    )
}

ImageLoader.defaultProps = {
    className: "",
    loadingClassName: css["img-loading"],
    loadedClassName: css["img-loaded"],
    transparentImage: css["transparentImage"],
}

ImageLoader.propTypes = {
    src: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    preload: PropTypes.bool,
    onClick: PropTypes.func,
    alt: PropTypes.string,
    title: PropTypes.string,
    optimize: PropTypes.bool,
    viewBox: PropTypes.string,
    className: PropTypes.string,
    transparentImage: PropTypes.any,
    loadedClassName: PropTypes.string,
    loadingClassName: PropTypes.string,
}

export default ImageLoader
